import { Injectable, signal } from '@angular/core';

const APP_URL_SCHEME = 'givo://';
const REDIRECT_TIMEOUT_MS = 1500;

/**
 * Tries to hand off a shared wishlist/profile link to the native Givo app via
 * its custom URL scheme, falling back to a dismissible banner when nothing
 * takes over the page within REDIRECT_TIMEOUT_MS (app not installed, or a
 * desktop browser that ignores the scheme).
 */
@Injectable({ providedIn: 'root' })
export class AppDeepLinkService {
  readonly appNotFound = signal(false);

  private lastPath = '';
  private timeoutId: number | null = null;

  isMobileDevice(): boolean {
    if (typeof navigator === 'undefined') return false;
    return /iphone|ipad|ipod|android/i.test(navigator.userAgent);
  }

  attemptOpen(path: string): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!this.isMobileDevice()) return;

    this.lastPath = path;
    this.appNotFound.set(false);
    this.clearPendingTimeout();

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        this.clearPendingTimeout();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange, { once: true });

    this.timeoutId = window.setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      this.appNotFound.set(true);
    }, REDIRECT_TIMEOUT_MS);

    window.location.href = `${APP_URL_SCHEME}${path}`;
  }

  retry(): void {
    if (this.lastPath) this.attemptOpen(this.lastPath);
  }

  dismiss(): void {
    this.appNotFound.set(false);
  }

  private clearPendingTimeout(): void {
    if (this.timeoutId != null) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}
